import { AlertCircle, CheckCircle2, Layers3, LoaderCircle } from "lucide-react"

import { useGalleryStudioBootstrap } from "../../hooks/use-gallery-studio-bootstrap"
import { cn } from "../../lib/utils"
import {
  GalleryStudioApiError,
  type GalleryStudioBootstrapInputs,
} from "../../services/use-gallery-studio"
import { Button } from "../ui/button"
import { Callout } from "../ui/callout"
import { GalleryStudioLoading } from "./gallery-studio-loading"

type BootstrapCandidate = GalleryStudioBootstrapInputs["candidates"][number]
type BootstrapWorkflow = ReturnType<typeof useGalleryStudioBootstrap>

export function isMissingGalleryStudioIntent(error: Error | null): boolean {
  return error instanceof GalleryStudioApiError && error.kind === "not_found"
}

function variantLabel(candidate: BootstrapCandidate): string {
  const labels = [candidate.flavor, candidate.size].filter(
    (label): label is string => label !== null && label.length > 0
  )
  return labels.length > 0 ? labels.join(" · ") : candidate.simpleSku
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null
}

function detailSkus(error: GalleryStudioApiError | null): string[] {
  if (!error || !isRecord(error.details)) {
    return []
  }
  for (const key of ["targetSimpleSkus", "unknownSimpleSkus"] as const) {
    if (key in error.details) {
      const value = error.details[key]
      if (Array.isArray(value) && value.every((item) => typeof item === "string")) {
        return value
      }
    }
  }
  return []
}

export function GalleryStudioBootstrapState({
  inputs,
  isLoading,
  error,
  createError,
  isCreating,
  onRetry,
  onCreate,
  onOpenPersisted,
}: {
  inputs: GalleryStudioBootstrapInputs | undefined
  isLoading: boolean
  error: Error | null
  createError: Error | null
  isCreating: boolean
  onRetry: () => void
  onCreate: (targetSimpleSkus: string[]) => Promise<unknown>
  onOpenPersisted: () => void
}) {
  if (isLoading) {
    return <GalleryStudioLoading />
  }
  if (error) {
    return (
      <Callout
        role="alert"
        tone="destructive"
        icon={<AlertCircle className="size-4" />}
        title="Gallery variants could not load"
        action={
          <Button size="sm" onClick={onRetry}>
            Retry
          </Button>
        }
      >
        <p className="mt-2 text-xs text-destructive">{error.message}</p>
      </Callout>
    )
  }
  if (!inputs) {
    return null
  }
  return (
    <GalleryStudioBootstrap
      key={inputs.candidates.map((candidate) => candidate.simpleSku).join("\u0000")}
      candidates={inputs.candidates}
      createError={createError instanceof GalleryStudioApiError ? createError : null}
      isCreating={isCreating}
      onCreate={onCreate}
      onOpenPersisted={onOpenPersisted}
    />
  )
}

export function GalleryStudioBootstrap({
  candidates,
  createError,
  isCreating,
  onCreate,
  onOpenPersisted,
}: {
  candidates: BootstrapCandidate[]
  createError: GalleryStudioApiError | null
  isCreating: boolean
  onCreate: (targetSimpleSkus: string[]) => Promise<unknown>
  onOpenPersisted: () => void
}) {
  const bootstrap = useGalleryStudioBootstrap(candidates, onCreate)

  if (bootstrap.step === "intro") {
    return (
      <Callout
        tone="info"
        icon={<Layers3 className="size-4" />}
        title="Gallery Studio has not started"
        action={
          <Button variant="primary" size="sm" onClick={bootstrap.start}>
            Start Gallery Studio
          </Button>
        }
      >
        <p className="mt-2 max-w-2xl text-sm text-foreground/80">
          Choose the exact variants this workspace will keep. No variant joins later automatically.
        </p>
      </Callout>
    )
  }

  return (
    <section className="rounded-xl border border-border bg-card p-5 shadow-card">
      <BootstrapHeader workflow={bootstrap} candidateCount={candidates.length} />

      <BootstrapCandidateList workflow={bootstrap} candidates={candidates} />

      <BootstrapCreateError error={createError} onOpenPersisted={onOpenPersisted} />

      <BootstrapActions workflow={bootstrap} isCreating={isCreating} />
    </section>
  )
}

function BootstrapHeader({
  workflow,
  candidateCount,
}: {
  workflow: BootstrapWorkflow
  candidateCount: number
}) {
  return (
    <div className="flex flex-wrap items-start justify-between gap-3">
      <div>
        <h3 className="text-[15px] font-semibold tracking-tight">
          {workflow.step === "select"
            ? "Choose frozen variants"
            : "Confirm Gallery Studio variants"}
        </h3>

        <p className="mt-1 text-xs text-muted-foreground">
          {`${workflow.selectedCandidates.length} of ${candidateCount} variants selected`}
        </p>

        {workflow.selectionError ? (
          <p role="alert" className="mt-2 text-xs text-destructive">
            {workflow.selectionError}
          </p>
        ) : null}
      </div>

      {workflow.step === "select" ? (
        <Button size="sm" onClick={workflow.toggleAll}>
          {workflow.selectedSkus.length === candidateCount ? "Clear all" : "Select all"}
        </Button>
      ) : null}
    </div>
  )
}

function BootstrapCandidateList({
  workflow,
  candidates,
}: {
  workflow: BootstrapWorkflow
  candidates: BootstrapCandidate[]
}) {
  const displayedCandidates = workflow.step === "select" ? candidates : workflow.selectedCandidates
  return (
    <div className="mt-4 divide-y divide-border/70 rounded-lg border border-border">
      {displayedCandidates.map((candidate) => (
        <label
          key={candidate.simpleSku}
          className={cn(
            "flex items-center gap-3 px-4 py-3",
            workflow.step === "select" && "cursor-pointer hover:bg-muted/40"
          )}
        >
          {workflow.step === "select" ? (
            <input
              type="checkbox"
              checked={workflow.selectedSkuSet.has(candidate.simpleSku)}
              onChange={() => workflow.toggleSku(candidate.simpleSku)}
              className="size-4 accent-info"
            />
          ) : (
            <CheckCircle2 className="size-4 shrink-0 text-success" aria-hidden="true" />
          )}

          <span className="min-w-0">
            <span className="block font-mono text-xs text-foreground">{candidate.simpleSku}</span>

            <span className="block text-sm text-muted-foreground">{variantLabel(candidate)}</span>
          </span>
        </label>
      ))}
    </div>
  )
}

function BootstrapCreateError({
  error,
  onOpenPersisted,
}: {
  error: GalleryStudioApiError | null
  onOpenPersisted: () => void
}) {
  if (!error) {
    return null
  }
  const skus = detailSkus(error)
  const hasPersistedTargets = error.kind === "conflict" && skus.length > 0
  return (
    <Callout className="mt-4" role="alert" tone="destructive" title={error.message}>
      {skus.length > 0 ? <p className="mt-2 text-xs text-destructive">{skus.join(", ")}</p> : null}

      {hasPersistedTargets ? (
        <Button className="mt-3" size="sm" onClick={onOpenPersisted}>
          Open persisted workspace
        </Button>
      ) : null}
    </Callout>
  )
}

function BootstrapActions({
  workflow,
  isCreating,
}: {
  workflow: BootstrapWorkflow
  isCreating: boolean
}) {
  const isSelectionInvalid = workflow.selectionError !== null
  return (
    <div className="mt-4 flex flex-wrap justify-between gap-2">
      <Button onClick={workflow.goBack} disabled={isCreating}>
        Back
      </Button>

      {workflow.step === "select" ? (
        <Button variant="primary" disabled={isSelectionInvalid} onClick={workflow.reviewSelection}>
          Review {workflow.selectedSkus.length} variants
        </Button>
      ) : (
        <Button
          variant="primary"
          disabled={isSelectionInvalid || isCreating}
          onClick={workflow.confirmCreate}
        >
          {isCreating ? <LoaderCircle className="size-4 animate-spin" /> : null}
          Confirm and create
        </Button>
      )}
    </div>
  )
}
